import { filter } from "@prismicio/client"
import type { IconName } from "@prismicio/editor-ui"
import { z } from "zod"

export type FilterType = keyof typeof filter

// We use the tuple/spread syntax to accommodate zod's enum type's expectations.
export const FilterTypes = Object.keys(filter) as [FilterType, ...FilterType[]]

// This type helper is a bit barbaric to look at, it recursively removes `Date` from a tuple.
type OmitDateFromTuple<T> = T extends [infer U, ...infer V]
  ? [
      U extends Date
        ? never
        : U extends (infer W)[]
          ? W extends Date
            ? never
            : W
          : U,
      ...OmitDateFromTuple<V>,
    ]
  : T

// Exclude `Date` from the arguments tuple, as it's not serializable.
type FilterArgTuples = {
  [K in FilterType]: OmitDateFromTuple<Parameters<(typeof filter)[K]>>
}

export type Filter = {
  [K in FilterType]: { type: K; args: FilterArgTuples[K] }
}[FilterType]

// Supported filters, should also be supported in `FilterConfigFieldset` component in `FilterConfigDialog.tsx`.
export const filters: {
  type: FilterType
  apiType: string
  icon: IconName
  args: string[]
}[] = [
  { type: "at", apiType: "at", icon: "tag", args: ["path", "value"] },
  { type: "not", apiType: "not", icon: "close", args: ["path", "value"] },
  { type: "any", apiType: "any", icon: "zoomOutMap", args: ["path", "values"] },
  { type: "in", apiType: "in", icon: "groupWork", args: ["path", "values"] },
  {
    type: "fulltext",
    apiType: "fulltext",
    icon: "notes",
    args: ["path", "value"],
  },
  { type: "has", apiType: "has", icon: "thumbUp", args: ["path"] },
  { type: "missing", apiType: "missing", icon: "thumbDown", args: ["path"] },
  {
    type: "geopointNear",
    apiType: "geopoint.near",
    icon: "public",
    args: ["path", "latitude", "longitude", "radius"],
  },
  {
    type: "numberLessThan",
    apiType: "lt",
    icon: "chevronLeft",
    args: ["path", "number"],
  },
  {
    type: "numberGreaterThan",
    apiType: "gt",
    icon: "chevronRight",
    args: ["path", "number"],
  },
  {
    type: "numberInRange",
    apiType: "number.inRange",
    icon: "settingsEthernet",
    args: ["path", "lowerLimit", "upperLimit"],
  },
  {
    type: "dateAfter",
    apiType: "date.after",
    icon: "calendarToday",
    args: ["path", "date"],
  },
  {
    type: "dateBefore",
    apiType: "date.before",
    icon: "calendarToday",
    args: ["path", "date"],
  },
  {
    type: "dateBetween",
    apiType: "date.between",
    icon: "dateRange",
    args: ["path", "startDate", "endDate"],
  },
]

// Excluding `Date` as it's not serializable.
export const FilterAnyArg = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.union([z.string(), z.number(), z.boolean()])),
])
export type FilterAnyArg = z.infer<typeof FilterAnyArg>
